import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
const negatives = ['This is not a generic gardening ebook.', 'This is not traditional soil gardening.', 'This is not a collection of random hydroponic tips.', 'And it is not hundreds of pages of complicated theory you will never use beside your plants.'];
const crops = ['fresh lettuce...', 'basil...', 'tomatoes...', 'peppers...', 'strawberries...', 'herbs...'];
const journey = [{
  step: 'BUILD',
  detail: 'Build the right system.'
}, {
  step: 'CONTROL',
  detail: 'Manage water, nutrients, pH and EC.'
}, {
  step: 'START',
  detail: 'Germinate and establish healthy plants.'
}, {
  step: 'GROW',
  detail: 'Understand each crop.'
}, {
  step: 'OPTIMIZE',
  detail: 'Control lighting, climate and plant development.'
}, {
  step: 'FIX',
  detail: 'Recognize problems and recover the system.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-sage-100 relative overflow-hidden" data-id="element-259" data-mp="2bvpecqt8sv3u">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-leaf" data-id="element-260" data-mp="2bvfiwnpfhvqr" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-261" data-mp="2gsz11yp8ysnz">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-262" data-mp="2kw5ebqas1k3y">
          <p className="text-sm md:text-base font-semibold text-forest/80 mb-4" data-id="element-263" data-mp="1t60m55grzss6">
            The Complete Visual Hydroponics Growing System for Home Growers
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-forest" data-id="element-264" data-mp="225w4bu8r0o1p">
            HYDROPONICS GROWER <span className="text-leaf" data-id="element-265" data-mp="26x5poin9l57s">READY</span>
          </h2>
          <p className="text-base md:text-lg text-forest font-semibold mb-1" data-id="element-266" data-mp="26yp403pd166x">
            A Complete 6-in-1 Visual Growing System
          </p>
          <p className="text-base md:text-lg text-charcoal/75 mb-10" data-id="element-267" data-mp="1efjmnrnpbcxx">
            6 Complete Volumes · Setup to Harvest · Crop Guides · Troubleshooting · 5 Practical
            Bonuses · 100% Visual
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-268" data-mp="2crmeqnrmxo91">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-sage-300" data-id="element-269" data-mp="2qjskrh12qeur">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-270" data-mp="1tv4jvxhhp88k">
                <X className="text-forest/50 shrink-0 mt-1" size={18} data-id="element-271" data-mp="27tuf17kr0xua" />
                <p className="text-charcoal/80 text-sm" data-id="element-272" data-mp="22tgfkdx7qw7v">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-leaf" data-id="element-273" data-mp="3j6wufzb43hcv">
            <div className="flex items-start gap-3" data-id="element-274" data-mp="3qrzb9a2224az">
              <Check className="text-leaf shrink-0 mt-1" size={20} data-id="element-275" data-mp="3qpe8mitxnkc6" />
              <div className="space-y-3" data-id="element-276" data-mp="3ctiueqahaxf4">
                <p className="text-forest font-medium text-base leading-relaxed" data-id="element-277" data-mp="3hr196xb6g6v1">
                  Hydroponics Grower Ready is a complete visual growing system created for people
                  who want to build and manage their own hydroponic garden at home.
                </p>
                <p className="text-charcoal text-sm leading-relaxed" data-id="element-278" data-mp="ec56yjyqj5a7">
                  Whether you want {crops.join(' ')} or other hydroponic crops...
                </p>
                <p className="text-forest font-semibold text-sm leading-relaxed" data-id="element-279" data-mp="4iy5vbb12bfx">
                  the system gives you a clear visual path from your first setup to your first
                  harvest.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-12" data-id="element-280" data-mp="34ly8y5rk2u78">
          <p className="text-forest/80 text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-281" data-mp="34i22jepzn2hb">
            The System Follows One Simple Growing Journey
          </p>
          <ol className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-282" data-mp="2vk8hx11l5x9z">
            {journey.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-sage-200" data-id="element-283" data-mp="2qm6r1txx4tny">
                <span className="text-2xl md:text-3xl font-extrabold tracking-tight text-leaf" data-id="element-284" data-mp="3jacb0eh5eemq">
                  {item.step}
                </span>
                <span className="text-sm text-charcoal/80 mt-1" data-id="element-285" data-mp="3eg82vrw5c0k2">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-forest" data-id="element-286" data-mp="atzn3cuslx5w">
            BUILD → CONTROL → START → GROW → OPTIMIZE → FIX
          </p>
        </div>
      </div>
    </section>;
}